import type {
  LandingPageHero,
  LandingPageBenefits,
  LandingPageApproach,
  LandingPageWhatYouGet,
  LandingPageKlartext,
  LandingPagePricingIntro,
  LandingPageContent,
} from './types';
import { DEFAULT_APPROACH, DEFAULT_CONTENT, DEFAULT_HERO, DEFAULT_PRICING_INTRO, DEFAULT_WHO_TRADES } from './default';
import svenBolzTeaching from '@/assets/images/sven_bolz_teaching.png';

const CASHFLOW_HERO: LandingPageHero = {
  headline: 'Dein Depot wächst – aber wann fängt es endlich an, dich auch zu bezahlen?',
  subheadlineLines: [
    'Willst du endlich Einnahmen, statt nur Buchgewinne?',
    'Nicht warten, bis du irgendwann verkaufst.',
    'Nicht nur hoffen, dass der Markt steigt.',
    'Sondern jetzt regelmäßig aus deinem Kapital ziehen.',
  ],
  descriptionLines: [
    'Ich zeige dir nicht, welche Aktie als nächstes läuft.',
    'Ich zeige dir, wie ich aus meinem Depot Woche für Woche Cashflow erzeuge.',
  ],
  ctaLabel: DEFAULT_HERO.ctaLabel,
};

const CASHFLOW_BENEFITS: LandingPageBenefits = {
  headline: 'Du kennst das wahrscheinlich:',
  painPoints: [
    'Dein Depot ist im Plus – aber das Kapital liegt nur rum, statt für dich zu arbeiten',
    'Du hast von Stillhalter- oder Optionsstrategien gehört – weißt aber nicht, wie du sauber starten sollst',
    'Du würdest gerne Einnahmen aus deinem Depot ziehen – ohne dafür ständig vor dem Bildschirm zu sitzen',
    'Du willst nicht ständig hoffen müssen, dass der Markt steigt – sondern auch in Seitwärtsphasen verdienen',
  ],
  conclusion: '…und fragst dich, ob das wirklich alles war, was dein Depot kann.',
  punchline: 'Investieren baut Vermögen. Cashflow bezahlt dein Leben.',
};

const CASHFLOW_APPROACH: LandingPageApproach = {
  philosophyLines: DEFAULT_APPROACH.philosophyLines,
  finalLine: DEFAULT_APPROACH.finalLine,
  bridgeLine: DEFAULT_APPROACH.bridgeLine,
  bulletItems: [
    'Wann macht ein Stillhalter-Setup wirklich Sinn – und wann ist das Risiko zu groß?',
    'Wann liefert ein Earnings-Trade Cashflow – und wann nur Stress?',
    'Wann arbeitet dein Depot für dich – und wann nur dein Hoffnungs-Muskel?',
  ],
};

const CASHFLOW_WHAT_YOU_GET: LandingPageWhatYouGet = {
  headline: 'Was du hier bekommst:',
  subheadline: 'Keine Theorie – sondern gelebte Praxis',
  highlight: 'Meine wöchentlichen Cashflow-Setups, Stillhalterstrategien und gezielten Earnings-Trades – inklusive Langzeit-Einstiegen und Swing-Trades. Klare Anleitungen, regelmäßige Q&A Videos und der Kontext, warum dieses Setup jetzt Sinn macht.',
};

const CASHFLOW_KLARTEXT: LandingPageKlartext = {
  title: 'Klartext',
  notForYouHeader: 'Das hier ist nichts für dich, wenn',
  notForYou: 'du noch nie investiert hast, kein Kapital aufbauen willst oder schnellen Reichtum suchst.',
  forYouHeader: 'Das hier ist für dich, wenn',
  forYouLines: [
    'du bereits ein Depot aufgebaut hast – und endlich Einnahmen daraus ziehen willst.',
    'du Stillhalter- und Optionsstrategien nutzen möchtest, aber sauber begleitet starten willst.',
    'du nicht nur auf Kursgewinne setzen willst – sondern auch in ruhigen Phasen Cashflow erzeugst.',
  ],
};

const CASHFLOW_PRICING_INTRO: LandingPagePricingIntro = {
  mainHeadline: DEFAULT_PRICING_INTRO.mainHeadline,
  beforeHeader: 'Du hörst auf:',
  beforeItems: [
    'Nur darauf zu hoffen, dass deine Kurse steigen',
    'Dein Kapital ohne Plan im Depot rumliegen zu lassen',
    'Optionen für zu kompliziert zu halten und sie nie zu nutzen',
  ],
  afterHeader: 'Und fängst an:',
  afterItems: [
    'Regelmäßigen Cashflow aus deinem bestehenden Depot zu ziehen',
    'Mit Stillhalter-Setups, Swing-Trades und Earnings strukturiert zu arbeiten',
    'Dein Depot wie ein Unternehmen aufzustellen – das jeden Monat liefert',
  ],
};

export const CASHFLOW_CONTENT: LandingPageContent = {
  ...DEFAULT_CONTENT,
  hero: CASHFLOW_HERO,
  benefits: CASHFLOW_BENEFITS,
  approach: CASHFLOW_APPROACH,
  whatYouGet: CASHFLOW_WHAT_YOU_GET,
  klartext: CASHFLOW_KLARTEXT,
  pricingIntro: CASHFLOW_PRICING_INTRO,
  whoTrades: { ...DEFAULT_WHO_TRADES, imageSrc: svenBolzTeaching },
};
